import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Sider from 'antd/lib/layout/Sider';
import ListBlock from './components/ListBlock';
import HeaderDropdown from './components/HeaderDropdown';
import { updateSidebarView } from '../../redux/actions/sidebar';
import { sidebarStepClickAction, clearSidebarStepClickAction } from '../../redux/actions/onboarding';
import useRedirect from '../../hooks/useRedirect';
import {
  Options,
  transactionsOptionsList,
  payoutsOptionsList,
  valueAddedFeaturesList,
  adminOptionList,
  opsAdminOptionList,
  edgeOptionList,
  edgeValueAddedFeaturesList,
  adminOptionListWithOps,
  edgeV3OptionList,
  merchantVersionUpdateTab,
  knowledgeBaseTab,
  partnerOptionList,
  partnerFeaturesList,
  DefaultStepsData,
  StepperStepCount,
  SidebarLabels,
  subscriptionOptionsList,
  rcuOptionSearchList,
  resetStepperFlows,
  DefaultStepperStepCount,
  CinLlpStepperStepCount,
  addBusinessVerificationToFlow,
  addUploadCompanyDocumentsToFlow,
  addSolePropDocumentsUploadToFlow,
  BPCLSolePropStepper,
  BPCLStepsData,
  shouldShowBusinessDetailsStep,
  STEP_LABEL_TO_BACKEND_STEP,
  normalizeStepStatus,
  StepStatus,
  adminDashboardOptionList,
  pgRouterAdminToolsList,
} from './sidebar.constant';
import imgURL from '../../lib/imgURL';
import {
  CleverTapEventNames,
  OPS_ADMIN,
  PRODUCTS,
  PRODUCT_ID_TO_NAME_MAP,
  UiRoutes,
} from '../../lib/constants';
import {
  pushClevertapEvent,
  clevertapEventConfigList,
  isCitiTheme,
} from '../../lib/analyticsUtil';
import Props from './typing';
import notify from '../../lib/notifiy';
import './style.scss';
import { checkPermissions, getDashboardThemeDetails, getIsAdminFromStore, getStepDetails as getStepDetailsHelper, getStepMerchantDetails, useFilteredSidebarOptions } from '../../lib/helper';
import { Icons } from '../SVGIcon/icons.constants';
import SVGIcon from '../SVGIcon';
import CustomStepper from '../CustomStepper';
import { Product } from '../../screens/Home/LandingPage/typing';
import { CURRENT_STEP_MAPPING, FLOW_CIN_LLP_STEP_MAPPING } from '../UserVerificationBox/constants';
import { getActivePgRouterAdminLinkValue } from '../../screens/Products/Plural/PgRouterAdmin/pgRouterAdminConfig';
import {
  getPartnerBrandLogo,
  isPartnerPage,
  shouldShowPartnerBrandHeader,
} from './sidebar.helpers';
import {
  getMerchantDetailsValue,
  isSelfGeneratedMerchantAccount,
  shouldShowEdgePaymentLinks,
} from '../../redux/selectors/merchantDetails';

const ADMIN_SIDEBAR_SCROLL_STORAGE_KEY = 'adminSidebarScrollTop';

const SideBar: FC<Props> = (props): JSX.Element => {
  const { push, generatePath, location: pathLocation } = useRedirect();
  const { isCollapsed } = props;
  const isCiti = isCitiTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [stepperData, setStepperData] = useState(DefaultStepsData);
  const [stepperRoutes, setStepperRoutes] = useState<string[]>(DefaultStepperStepCount);
  const pendingRouteRef = useRef<string | null>(null);
  const currentProduct = pathLocation?.pathname?.split('/')[1];
  const onSelfServePage = currentProduct === 'user-verification';
  const hideStepper = pathLocation?.pathname?.split('/').includes('soft-rejection');
  const [isParentMerchant, setIsParentMerchant] = useState<boolean>(false);
  const [isPgRouterAdminGroupOpen, setIsPgRouterAdminGroupOpen] = useState<boolean>(false);
  const adminMenuScrollRef = useRef<HTMLDivElement | null>(null);
  const adminMenuScrollTopRef = useRef<number | null>(null);
  const pgRouterAdminGroupRef = useRef<HTMLLIElement | null>(null);
  const activePgRouterAdminItemRef = useRef<HTMLLIElement | null>(null);
  const isTransactionMerchant = useSelector((state: any) => state?.loginReducer?.loginState?.isTransactingMerchant ?? false);
  const permissionState = useSelector((state: any) => state?.loginReducer?.permissionState ?? {});
  const isSelfGeneratedAccount = isSelfGeneratedMerchantAccount(
    props.accountSource
  );
  const shouldShowPaymentLinks = shouldShowEdgePaymentLinks(
    props?.edgeMerchantPaymentLinkEnabled,
    props.accountSource
  );
  const isOtherProductTabEnabled =
    shouldShowPaymentLinks || props?.isBrandWalletEnabled;
  const checkoutInsightsBreakdownState = useSelector((state: any) => state?.overviewReducer?.checkoutInsightsPaymentModeBreakdownState);
  // TODO: re-enable when Cardless EMI upsell card is ready to ship
  // const merchantPaymentModes: string[] = useSelector(
  //   (state: any) => state?.settingsReducer?.getCheckoutVersionState?.data?.[8]?.merchantPaymentModes ?? []
  // );
  // const checkoutVersionLoaded: boolean = useSelector(
  //   (state: any) => state?.settingsReducer?.getCheckoutVersionState?.isSuccess ?? false
  // );
  // const showCardlessEmiPromo =
  //   pathLocation?.pathname === UiRoutes.EDGE_OVERVIEW_CHECKOUT_INSIGHTS &&
  //   checkoutVersionLoaded &&
  //   Array.isArray(merchantPaymentModes) &&
  //   !merchantPaymentModes.includes('CARDLESS');
  const showCardlessEmiPromo = false;
  const partnerBrandLogo = getPartnerBrandLogo({
    isPartnerPage: isPartnerPage(pathLocation?.pathname),
    themingDetails: props?.themingDetails,
  });
  const showPartnerBrandHeader = shouldShowPartnerBrandHeader({
    isAdmin: props.isAdmin,
    isPartnerMerchant: props.isPartnerMerchant,
    merchantId: props.merchantId,
    partnerId: props.partnerId,
  });
  const canAccessPgRouterAdmin = useMemo(
    () => checkPermissions('PAYPREDICT_MAKER') || checkPermissions('PAYPREDICT_CHECKER') || checkPermissions('OBSERVABILITY READ'),
    [permissionState]
  );
  const hasPaypredictPermission = useMemo(
    () => checkPermissions('PAYPREDICT_MAKER') || checkPermissions('PAYPREDICT_CHECKER'),
    [permissionState]
  );
  const shouldRenderAdminDashboardSection =
    adminDashboardOptionList.length > 0 || canAccessPgRouterAdmin;

  const preserveAdminSidebarScroll = useCallback(() => {
    if (typeof window === 'undefined' || !adminMenuScrollRef.current) return;

    adminMenuScrollTopRef.current = adminMenuScrollRef.current.scrollTop;
    window.sessionStorage.setItem(
      ADMIN_SIDEBAR_SCROLL_STORAGE_KEY,
      String(adminMenuScrollTopRef.current)
    );
  }, []);

  const restoreAdminSidebarScroll = useCallback(() => {
    if (typeof window === 'undefined' || !adminMenuScrollRef.current) return;

    const storedScrollTopValue = window.sessionStorage.getItem(
      ADMIN_SIDEBAR_SCROLL_STORAGE_KEY
    );

    if (storedScrollTopValue === null && adminMenuScrollTopRef.current === null) return;

    const storedScrollTop = adminMenuScrollTopRef.current ?? Number(storedScrollTopValue);
    if (!Number.isFinite(storedScrollTop)) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (adminMenuScrollRef.current) {
          adminMenuScrollRef.current.scrollTop = storedScrollTop;
        }
      });
    });
  }, []);

  const handleAdminSidebarScroll = useCallback(() => {
    if (typeof window === 'undefined' || !adminMenuScrollRef.current) return;

    adminMenuScrollTopRef.current = adminMenuScrollRef.current.scrollTop;
    window.sessionStorage.setItem(
      ADMIN_SIDEBAR_SCROLL_STORAGE_KEY,
      String(adminMenuScrollTopRef.current)
    );
  }, []);

  const navigateFromAdminSidebar = useCallback((path: string) => {
    preserveAdminSidebarScroll();
    push(generatePath(path));
  }, [generatePath, preserveAdminSidebarScroll, push]);

  useEffect(() => {
    restoreAdminSidebarScroll();
  }, [
    isCollapsed,
    isPgRouterAdminGroupOpen,
    pathLocation?.pathname,
    restoreAdminSidebarScroll,
  ]);

  useEffect(() => {
    resetStepperFlows();
    const merchantDetails = getStepMerchantDetails(props?.stepperDetails, 'PAN_GST_DETAILS');
    const panType = merchantDetails?.pan_details?.pan_type;

    if (shouldShowBusinessDetailsStep(props?.stepperDetails?.data)) {
      addBusinessVerificationToFlow();
    }

    if (panType && Object.keys(props?.stepperDetails?.data).length > 0) {
      if (panType != 'INDIVIDUAL') {
        addUploadCompanyDocumentsToFlow();
      }
      else {
        addSolePropDocumentsUploadToFlow();
      }
    }

    let stepperStepCount = DefaultStepperStepCount;

    const bankMode = props?.stepperDetails?.data?.onboarding_configurations?.bank_details_mode || 'SELF_MANAGED';
    const isParentManaged =
      bankMode === 'PARENT_MANAGED' && !!props?.stepperDetails?.data?.parent_provided_data?.bank_details;

    if (panType && panType === 'INDIVIDUAL' && isParentManaged) {
      stepperStepCount = BPCLSolePropStepper;
      setStepperData([...BPCLStepsData]);
    }

    if (stepperStepCount === DefaultStepperStepCount) {
      setStepperData([...DefaultStepsData]);
    }

    setStepperRoutes([...stepperStepCount]);

    const currentPathname = pathLocation?.pathname;
    const routesSnapshot = [...stepperStepCount];
    const matchedIndex = routesSnapshot.indexOf(currentPathname);


    if (matchedIndex > -1) {
      setCurrentStep(matchedIndex);
    } else {
      // Don't reset to 0 if we're on a self-serve page — keep current step
      if (!currentPathname?.startsWith('/user-verification')) {
        setCurrentStep(0);
      }
    }

  }, [props?.stepperDetails?.data, pathLocation?.pathname]);

  const selectedProduct: Product = useSelector(
    (state: any) => state?.loginReducer?.loginState?.selectedProduct
  );

  const filteredOptionsV2 = useFilteredSidebarOptions(edgeOptionList).filter(
    (item: Options) => {
      if (item.value === UiRoutes.EDGE_DISPUTES && !props?.isDisputesEnabled) return false;
      return !item.isHidden;
    }
  );

  const filteredOptionsV3 = useFilteredSidebarOptions(edgeV3OptionList).filter(
  (item: Options) => {
    if (item.value === UiRoutes.V3_PAYMENTS && !props?.isTransactionMerchant) return false;
    if (item.value === UiRoutes.EDGE_DISPUTES && !props?.isDisputesEnabled) return false;
    return !item.isHidden; 
  }
);
  const filteredPartnerOptions = useFilteredSidebarOptions(partnerFeaturesList, props?.isAdmin);

  useEffect(() => {
    if (props?.isParentMerchantOne) {
      setIsParentMerchant(props?.isParentMerchantOne);
    }
  }, [props?.isParentMerchantOne]);

  // Compute step statuses from lead_progress
  const stepStatuses = useMemo((): (StepStatus | null)[] | undefined => {
    const leadProgress = props?.stepperDetails?.data?.lead_progress;
    const currentStepFromApi = props?.stepperDetails?.data?.current_step;

    const getFallbackLockedStatuses = (): StepStatus[] | undefined => {
      const activeIndexFromApi = currentStepFromApi
        ? stepperData.findIndex(
          (stepLabel) => STEP_LABEL_TO_BACKEND_STEP[stepLabel] === currentStepFromApi
        )
        : -1;
      const activeIndexFromRoute = stepperRoutes.indexOf(pathLocation?.pathname);
      const activeIndex =
        activeIndexFromApi > -1
          ? activeIndexFromApi
          : activeIndexFromRoute > -1
            ? activeIndexFromRoute
            : currentStep;

      if (activeIndex < 0 || activeIndex >= stepperData.length) return undefined;

      return stepperData.map((_, index) => (
        index === activeIndex ? 'IN_PROGRESS' : 'LOCKED'
      ));
    };

    if (!Array.isArray(leadProgress) || leadProgress.length === 0) {
      return getFallbackLockedStatuses();
    }

    const statuses = stepperData.map((label) => {
      const backendStepName = STEP_LABEL_TO_BACKEND_STEP[label];
      if (!backendStepName) return null;

      const stepObj = leadProgress?.find(
        (s: any) => s?.step_name === backendStepName
      );
      if (!stepObj) return null;

      const status = normalizeStepStatus(stepObj?.status);

      if (
        backendStepName === 'MDR_ACCEPTANCE' &&
        status === 'COMPLETED' &&
        currentStepFromApi &&
        currentStepFromApi !== 'MDR_ACCEPTANCE'
      ) {
        return 'COMPLETED_LOCKED';
      }

      return status;
    });

    // If every status is null (no steps matched), treat as no data
    if (statuses.every((status) => status === null)) return getFallbackLockedStatuses();

    // Lock all steps except KYC & Sign Agreement when current_step is KYC_AGREEMENT_SIGN
    if (currentStepFromApi === 'KYC_AGREEMENT_SIGN') {
      statuses.forEach((_, i) => {
        const backendStep = STEP_LABEL_TO_BACKEND_STEP[stepperData[i]];
        if (backendStep && backendStep !== 'KYC_AGREEMENT_SIGN') {
          statuses[i] = statuses[i] === 'COMPLETED' || statuses[i] === 'COMPLETED_LOCKED'
            ? 'COMPLETED_LOCKED'
            : 'LOCKED';
        }
      });
    }

    return statuses;
  }, [
    props?.stepperDetails?.data?.lead_progress,
    props?.stepperDetails?.data?.current_step,
    stepperData,
    stepperRoutes,
    pathLocation?.pathname,
    currentStep,
  ]);

  // Handle step click in the sidebar stepper
  const handleStepClick = useCallback((index: number) => {
    if (stepStatuses?.[index] === 'LOCKED' || stepStatuses?.[index] === 'COMPLETED_LOCKED') return;

    const targetRoute = stepperRoutes[index];
    if (!targetRoute) return;

    const nextStepLabel = stepperData[index];
    const nextStepName = STEP_LABEL_TO_BACKEND_STEP[nextStepLabel] || '';

    if (nextStepName) {
      // Store intended route for DOCUMENTS_UPLOAD steps (ambiguous backend response)
      if (nextStepLabel === 'Business verification' || nextStepLabel === 'Upload documents') {
        pendingRouteRef.current = targetRoute;
      }
      props.sidebarStepClickAction({
        target_step: nextStepName,
      });
    } else {
      // Fallback: route directly if no backend step mapping
      push(generatePath(targetRoute));
    }
  }, [stepStatuses, stepperRoutes, stepperData, push, generatePath, props?.stepperDetails?.data?.lead_id]);

  // Handle sidebar click API response
  useEffect(() => {
    if (props?.sidebarStepClickState?.isSuccess) {
      const responseData = props?.sidebarStepClickState;
      const responseStep = responseData?.current_step;
      if (responseStep) {
        // For DOCUMENTS_UPLOAD, route is ambiguous (BPCL upload-documents vs upload-documents-company).
        // Prefer the route the user just clicked (pendingRouteRef). If unavailable, infer from the
        // current stepperRoutes by looking for the company-documents route first, then BPCL.
        if (responseStep === 'DOCUMENTS_UPLOAD') {
          const solePropRoute = stepperRoutes.find(
            (r) => r === '/user-verification/upload-documents-sole-prop'
          );
          const companyRoute = stepperRoutes.find(
            (r) => r === '/user-verification/upload-documents-company'
          );
          const bpclRoute = stepperRoutes.find(
            (r) => r === '/user-verification/upload-documents'
          );
          const targetRoute =
            pendingRouteRef.current || solePropRoute || companyRoute || bpclRoute;
          if (targetRoute) {
            push(generatePath(targetRoute));
          }
          pendingRouteRef.current = null;
        } else {
          const routeKey = (CURRENT_STEP_MAPPING as Record<string, string>)[responseStep]
            || (FLOW_CIN_LLP_STEP_MAPPING as Record<string, string>)[responseStep];
          const route = routeKey ? (UiRoutes as unknown as Record<string, string>)[routeKey] : null;
          if (route) {
            push(generatePath(route));
          }
        }
      }
      props.clearSidebarStepClickAction();
    }
    if (props?.sidebarStepClickState?.isFailure) {
      const errorMsg = props?.sidebarStepClickState?.errorMessage || 'Unable to navigate to the selected step';
      notify({
        message: 'Navigation failed',
        description: errorMsg,
        type: 'error',
      });
      props.clearSidebarStepClickAction();
    }
  }, [props?.sidebarStepClickState?.isSuccess, props?.sidebarStepClickState?.isFailure]);

  const getCompleteProfileSectionSideBar = () => {
    return <>
      <div className='complete-profile-sidebar'>
        <h4>{SidebarLabels.COMPLETE_PROFILE}</h4>
        <p>{SidebarLabels.SUBMIT_DETAILS}</p>
      </div>
      {!hideStepper && (
        <CustomStepper
          numberOfSteps={stepperData.length}
          stepperData={stepperData}
          currentStep={currentStep}
          stepStatuses={stepStatuses}
          onStepClick={handleStepClick}
        />
      )}
    </>;
  };
  const hideSettlement = (isSelfServe, isLiveMerchant) => {
    return isSelfServe && !isLiveMerchant;
  };
  const getSideBarMenusForEdgeVersions = (version) => {
    let menuItems;
    if (version === 'V3')
      menuItems = (
        <>
          <div
            className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'
              } customScroll ${!isCollapsed ? 'horizontal-padding' : ''}`}
          >
            <div className='manu-block'>
              <ul className='list-unstyled components'>
                <ListBlock
                  className={
                    (pathLocation.pathname.indexOf(UiRoutes.EDGE_OVERVIEW) >
                      -1 &&
                      'active') ||
                    ''
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    push(generatePath(UiRoutes.EDGE_OVERVIEW));
                    pushClevertapEvent({
                      eventName:
                        CleverTapEventNames.SIDEBAR_TAB_CLICKED_PAYMENT,
                    });
                  }}
                  isCollapsed={isCollapsed}
                  label={'Overview'}
                  imgSrc={imgURL['overview-icon']}
                  imgAlt={'Overview'}
                  icon={Icons.overviewSidebar}
                />
                {isParentMerchant && (
                  <ListBlock
                    className={
                      (pathLocation.pathname.indexOf(UiRoutes.ALL_MERCHANTS) >
                        -1 &&
                        'active') ||
                      ''
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      push(generatePath(UiRoutes.ALL_MERCHANTS));
                    }}
                    isCollapsed={isCollapsed}
                    label={'All Merchants'}
                    imgSrc={imgURL['overview-icon']}
                    imgAlt={'All Merchants'}
                    icon={Icons.overviewSidebar}
                  />
                )}
              </ul>
            </div>
            <div className='manu-block'>
              {!isCollapsed && (
                <h6 className='category-heading'>TRANSACTIONS</h6>
              )}
              <ul className='list-unstyled components'>
                {filteredOptionsV3
                  .map((item: Options, index: number) => {
                    if (item.value === UiRoutes.UPDATED_EDGE_SETTLEMENTS) {
                      return !hideSettlement(
                        props.isSelfServe,
                        props.isLiveMerchant
                      ) ? (
                        <ListBlock
                          className={
                            (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                          }
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!props.isAdmin)
                              pushClevertapEvent(
                                clevertapEventConfigList[item.clevertapEventName ?? '']
                              );
                            push(generatePath(`${item.value}`));
                          }}
                          isCollapsed={isCollapsed}
                          label={item.label}
                          imgAlt={item.label}
                          icon={item.iconName}
                          imgSrc={item.icon}
                        />
                      ) : <></>;
                    } else {
                      return (
                        <ListBlock
                          className={
                            (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                          }
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!props.isAdmin)
                              pushClevertapEvent(
                                clevertapEventConfigList[item.clevertapEventName ?? '']
                              );
                            push(generatePath(`${item.value}`));
                          }}
                          isCollapsed={isCollapsed}
                          label={item.label}
                          imgSrc={item.icon}
                          imgAlt={item.label}
                          icon={item.iconName}
                        />
                      );
                    }
                  })}
              </ul>

            </div>
            {isOtherProductTabEnabled && <div className='manu-block'>
              {!isCollapsed && (
                <h6 className='category-heading'>OTHER PRODUCTS</h6>
              )}
              <ul className='list-unstyled components'>
                {edgeValueAddedFeaturesList(
                  props.edgeMerchantPaymentLinkEnabled,
                  props?.isBrandWalletEnabled,
                  isSelfGeneratedAccount
                ).map((item: Options, index: number) => {
                  return (
                    !item.isHidden && (
                      <ListBlock
                        className={
                          (pathLocation.pathname.indexOf(item.value) > -1 &&
                            'active') ||
                          ''
                        }
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!props.isAdmin)
                            pushClevertapEvent({
                              eventName: item.clevertapEventName ?? '',
                            });
                          push(generatePath(`${item.value}`));
                        }}
                        isCollapsed={isCollapsed}
                        label={item.label}
                        imgSrc={item.icon}
                        imgAlt={item.label}
                        icon={item.iconName}
                      />
                    )
                  );
                })}
              </ul>
            </div>}
            <div className='manu-block last last-element'>
              <ul className='list-unstyled components'>
                <ListBlock
                  className={
                    (pathLocation.pathname.indexOf(UiRoutes.EDGE_SETTINGS) >
                      -1 &&
                      'active') ||
                    ''
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    if (!props.isAdmin)
                      pushClevertapEvent({
                        eventName:
                          CleverTapEventNames.SIDEBAR_TAB_CLICKED_SETTINGS,
                      });
                    push(generatePath(UiRoutes.EDGE_SETTINGS));
                  }}
                  isCollapsed={isCollapsed}
                  label={'Settings'}
                  imgSrc={imgURL['setting-icon']}
                  imgAlt={'Settings'}
                  icon={Icons.settingsSidebar}
                />
              </ul>
            </div>
            {!isCollapsed && showCardlessEmiPromo && (
              <div className='cardless-emi-promo'>
                <div className='cardless-emi-promo__header'>
                  <div className='cardless-emi-promo__text'>
                    <span className='cardless-emi-promo__title'>Cardless EMI</span>
                    <span className='cardless-emi-promo__desc'>Offer easy EMIs without a credit card and give customers more ways to pay.</span>
                  </div>
                  <svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g opacity="0.3"><g filter="url(#filter0_d_promo)"><g clipPath="url(#clip0_promo)"><rect x="5.33325" y="5.3335" width="32" height="32" rx="16" fill="white" /><g filter="url(#filter1_f_promo)"><ellipse cx="10.617" cy="15.3308" rx="11.9434" ry="17.0957" transform="rotate(35.4043 10.617 15.3308)" fill="#89ED0F" /></g><g filter="url(#filter3_f_promo)"><circle cx="28.4009" cy="32.4263" r="17.0957" fill="#92DE00" /></g><g filter="url(#filter4_f_promo)"><circle cx="31.7014" cy="21.8511" r="7.11816" fill="#E2FDAE" /></g><path d="M26.5178 15.2202H18.7639L18.6907 15.2974L13.4377 20.8081L13.0344 21.23H20.7649V29.3521L21.1956 28.8999L26.4485 23.3892L26.5178 23.3169V15.2202Z" fill="white" stroke="#ECF9D4" strokeWidth="0.5" /></g><rect x="6.08325" y="6.0835" width="30.5" height="30.5" rx="15.25" stroke="#77D063" strokeOpacity="0.1" strokeWidth="1.5" /></g></g>
                    <defs>
                      <filter id="filter0_d_promo" x="0" y="0" width="43" height="43" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" /><feOffset /><feGaussianBlur stdDeviation="2.66667" /><feComposite in2="hardAlpha" operator="out" /><feColorMatrix type="matrix" values="0 0 0 0 0.675008 0 0 0 0 0.81646 0 0 0 0 0.503552 0 0 0 0.37 0" /><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" /><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" /></filter>
                      <filter id="filter1_f_promo" x="-12" y="-9" width="46" height="50" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="4.6" result="effect1_foregroundBlur" /></filter>
                      <filter id="filter3_f_promo" x="2" y="6" width="53" height="53" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="4.6" result="effect1_foregroundBlur" /></filter>
                      <filter id="filter4_f_promo" x="17" y="7" width="29" height="29" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="3.5" result="effect1_foregroundBlur" /></filter>
                      <clipPath id="clip0_promo"><rect x="5.33325" y="5.3335" width="32" height="32" rx="16" fill="white" /></clipPath>
                    </defs>
                  </svg>
                </div>
                <button className='cardless-emi-promo__cta'>Contact Sales</button>
              </div>
            )}
            {/* <div className='manu-block'>
              {!isCollapsed && (
                <h6 className='category-heading'>TRANSACTIONS</h6>
              )}
              <ul className='list-unstyled components'>
                {edgeOptionList.map((item: Options, index: number) => {
                  return (
                    <ListBlock
                      className={
                        (pathLocation.pathname.indexOf(item.value) > -1 &&
                          'active') ||
                        ''
                      }
                      key={index}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!props.isAdmin)
                          pushClevertapEvent(
                            clevertapEventConfigList[
                              item.clevertapEventName ?? ''
                            ]
                          );
                        push(generatePath(`${item.value}`));
                      }}
                      isCollapsed={isCollapsed}
                      label={item.label}
                      imgSrc={item.icon}
                      imgAlt={item.label}
                    />
                  );
                })}
              </ul>
            </div> */}
            {/* <div className='manu-block last last-element'>
              <ul className='list-unstyled components'>
                <ListBlock
                  className={
                    (pathLocation.pathname.indexOf(UiRoutes.EDGE_SETTINGS) >
                      -1 &&
                      'active') ||
                    ''
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    if (!props.isAdmin)
                      pushClevertapEvent(
                        clevertapEventConfigList.SIDEBAR_TAB_CLICKED_SETTINGS
                      );
                    push(generatePath(UiRoutes.EDGE_SETTINGS));
                  }}
                  isCollapsed={isCollapsed}
                  label={'Settings'}
                  imgSrc={imgURL['setting-icon']}
                  imgAlt={'Settings'}
                />
              </ul>
            </div> */}
          </div>
        </>
      );
    else if (version === 'V2') {
      menuItems = (
        <>
          <div
            className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'} customScroll ${!isCollapsed ? 'horizontal-padding' : ''
              }`}
          >
            <div className='manu-block'>
              <ul className='list-unstyled components'>
                <ListBlock
                  className={
                    (pathLocation.pathname.indexOf(UiRoutes.EDGE_OVERVIEW) >
                      -1 &&
                      'active') ||
                    ''
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    push(generatePath(UiRoutes.EDGE_OVERVIEW));
                    pushClevertapEvent({
                      eventName:
                        CleverTapEventNames.SIDEBAR_TAB_CLICKED_PAYMENT,
                    });
                  }}
                  isCollapsed={isCollapsed}
                  label={'Overview'}
                  imgSrc={imgURL['overview-icon']}
                  imgAlt={'Overview'}
                  icon={Icons.overviewSidebar}
                />
                {isParentMerchant && (
                  <ListBlock
                    className={
                      (pathLocation.pathname.indexOf(UiRoutes.ALL_MERCHANTS) >
                        -1 &&
                        'active') ||
                      ''
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      push(generatePath(UiRoutes.ALL_MERCHANTS));
                    }}
                    isCollapsed={isCollapsed}
                    label={'All Merchants'}
                    imgSrc={imgURL['overview-icon']}
                    imgAlt={'All Merchants'}
                    icon={Icons.overviewSidebar}
                  />
                )}
              </ul>
            </div>
            <div className='manu-block'>
              {!isCollapsed && (
                <h6 className='category-heading'>TRANSACTIONS</h6>
              )}
              <ul className='list-unstyled components'>
                {filteredOptionsV2
                  .map((item: Options, index: number) => {
                    return (
                      <ListBlock
                        className={
                          (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                        }
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!props.isAdmin)
                            pushClevertapEvent({
                              eventName: item.clevertapEventName,
                            });
                          push(generatePath(`${item.value}`));
                        }}
                        isCollapsed={isCollapsed}
                        label={item.label}
                        imgSrc={item.icon}
                        imgAlt={item.label}
                        icon={item.iconName}
                      />
                    );
                  })}
              </ul>
            </div>
            {shouldShowPaymentLinks && (
              <div className='manu-block'>
                {!isCollapsed && (
                  <h6 className='category-heading'>OTHER PRODUCTS</h6>
                )}
                <ul className='list-unstyled components'>
                  {edgeValueAddedFeaturesList(
                    props.edgeMerchantPaymentLinkEnabled,
                    false,
                    isSelfGeneratedAccount
                  ).map((item: Options, index: number) => {
                    return (
                      !item.isHidden && (
                        <ListBlock
                          className={
                            (pathLocation.pathname.indexOf(item.value) > -1 &&
                              'active') ||
                            ''
                          }
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!props.isAdmin)
                              pushClevertapEvent({
                                eventName: item.clevertapEventName ?? '',
                              });
                            push(generatePath(`${item.value}`));
                          }}
                          isCollapsed={isCollapsed}
                          label={item.label}
                          imgSrc={item.icon}
                          imgAlt={item.label}
                          icon={item.iconName}
                        />
                      )
                    );
                  })}
                </ul>
              </div>
            )}
            <div className='manu-block last last-element'>
              <ul className='list-unstyled components'>
                <ListBlock
                  className={
                    (pathLocation.pathname.indexOf(UiRoutes.EDGE_SETTINGS) >
                      -1 &&
                      'active') ||
                    ''
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    if (!props.isAdmin)
                      pushClevertapEvent({
                        eventName:
                          CleverTapEventNames.SIDEBAR_TAB_CLICKED_SETTINGS,
                      });
                    push(generatePath(UiRoutes.EDGE_SETTINGS));
                  }}
                  isCollapsed={isCollapsed}
                  label={'Settings'}
                  imgSrc={imgURL['setting-icon']}
                  imgAlt={'Settings'}
                  icon={Icons.settingsSidebar}

                />
              </ul>
            </div>
            {!isCollapsed && showCardlessEmiPromo && (
              <div className='cardless-emi-promo'>
                <div className='cardless-emi-promo__header'>
                  <div className='cardless-emi-promo__text'>
                    <span className='cardless-emi-promo__title'>Cardless EMI</span>
                    <span className='cardless-emi-promo__desc'>Offer easy EMIs without a credit card and give customers more ways to pay.</span>
                  </div>
                  <svg width="43" height="43" viewBox="0 0 43 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g opacity="0.3"><g filter="url(#filter0_d_promo)"><g clipPath="url(#clip0_promo)"><rect x="5.33325" y="5.3335" width="32" height="32" rx="16" fill="white" /><g filter="url(#filter1_f_promo)"><ellipse cx="10.617" cy="15.3308" rx="11.9434" ry="17.0957" transform="rotate(35.4043 10.617 15.3308)" fill="#89ED0F" /></g><g filter="url(#filter3_f_promo)"><circle cx="28.4009" cy="32.4263" r="17.0957" fill="#92DE00" /></g><g filter="url(#filter4_f_promo)"><circle cx="31.7014" cy="21.8511" r="7.11816" fill="#E2FDAE" /></g><path d="M26.5178 15.2202H18.7639L18.6907 15.2974L13.4377 20.8081L13.0344 21.23H20.7649V29.3521L21.1956 28.8999L26.4485 23.3892L26.5178 23.3169V15.2202Z" fill="white" stroke="#ECF9D4" strokeWidth="0.5" /></g><rect x="6.08325" y="6.0835" width="30.5" height="30.5" rx="15.25" stroke="#77D063" strokeOpacity="0.1" strokeWidth="1.5" /></g></g>
                    <defs>
                      <filter id="filter0_d_promo" x="0" y="0" width="43" height="43" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" /><feOffset /><feGaussianBlur stdDeviation="2.66667" /><feComposite in2="hardAlpha" operator="out" /><feColorMatrix type="matrix" values="0 0 0 0 0.675008 0 0 0 0 0.81646 0 0 0 0 0.503552 0 0 0 0.37 0" /><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" /><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" /></filter>
                      <filter id="filter1_f_promo" x="-12" y="-9" width="46" height="50" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="4.6" result="effect1_foregroundBlur" /></filter>
                      <filter id="filter3_f_promo" x="2" y="6" width="53" height="53" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="4.6" result="effect1_foregroundBlur" /></filter>
                      <filter id="filter4_f_promo" x="17" y="7" width="29" height="29" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB"><feFlood floodOpacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="3.5" result="effect1_foregroundBlur" /></filter>
                      <clipPath id="clip0_promo"><rect x="5.33325" y="5.3335" width="32" height="32" rx="16" fill="white" /></clipPath>
                    </defs>
                  </svg>
                </div>
                <button className='cardless-emi-promo__cta'>Contact Sales</button>
              </div>
            )}
          </div>{' '}
        </>
      );
    } else menuItems = <></>;
    return menuItems;
  };

  const getSelectedProduct = () => {
    if (onSelfServePage) return PRODUCTS.COMPLETE_PROFILE;

    // Return product field if available, otherwise map from ID
    if (selectedProduct?.product) return selectedProduct.product;

    // Simple ID to product mapping as fallback
    return PRODUCT_ID_TO_NAME_MAP[selectedProduct?.id as keyof typeof PRODUCT_ID_TO_NAME_MAP];
  };

  const sidebarMenu = () => {
    const selectedProduct = getSelectedProduct();
    switch (selectedProduct) {
      case PRODUCTS.ROUTER:
        return (
          <>
            <div
              className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'} customScroll ${!isCollapsed ? 'horizontal-padding' : ''
                }`}
            >
              <div className='manu-block'>
                <ul className='list-unstyled components'>
                  <ListBlock
                    className={
                      (pathLocation.pathname.indexOf(UiRoutes.OVERVIEW) > -1 &&
                        'active') ||
                      ''
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      push(generatePath(UiRoutes.OVERVIEW));
                    }}
                    isCollapsed={isCollapsed}
                    label={'Overview'}
                    imgSrc={imgURL['overview-icon']}
                    imgAlt={'Overview'}
                    icon={Icons.overviewSidebar}
                  />
                </ul>
              </div>
              <div className='manu-block'>
                {!isCollapsed && (
                  <h6 className='category-heading'>TRANSACTIONS</h6>
                )}
                <ul className='list-unstyled components'>
                  {transactionsOptionsList.map(
                    (item: Options, index: number) => {
                      return (
                        <ListBlock
                          className={
                            (pathLocation.pathname.indexOf(item.value) > -1 &&
                              'active') ||
                            ''
                          }
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!props.isAdmin)
                              pushClevertapEvent(
                                clevertapEventConfigList[
                                item.clevertapEventName ?? ''
                                ]
                              );
                            push(generatePath(`${item.value}`));
                          }}
                          isCollapsed={isCollapsed}
                          label={item.label}
                          imgSrc={item.icon}
                          imgAlt={item.label}
                          icon={item.iconName}
                        />
                      );
                    }
                  )}
                </ul>
              </div>

              <div className='manu-block'>
                <ul className='list-unstyled components'>
                  {valueAddedFeaturesList(
                    props.merchantTPVEnabled,
                    props.merchantSubscriptionEnabled
                  ).map((item: Options, index: number) => {
                    return (
                      !item.isHidden && (
                        <ListBlock
                          className={
                            (pathLocation.pathname.indexOf(item.value) > -1 &&
                              'active') ||
                            ''
                          }
                          key={index}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!props.isAdmin)
                              pushClevertapEvent(
                                clevertapEventConfigList[
                                item.clevertapEventName ?? ''
                                ]
                              );
                            push(generatePath(`${item.value}`));
                          }}
                          isCollapsed={isCollapsed}
                          label={item.label}
                          imgSrc={item.icon}
                          imgAlt={item.label}
                          icon={item.iconName}
                        />
                      )
                    );
                  })}
                </ul>
              </div>
              <div className='manu-block last last-element'>
                <ul className='list-unstyled components'>
                  <ListBlock
                    className={
                      (pathLocation.pathname.indexOf(UiRoutes.SETTINGS) > -1 &&
                        'active') ||
                      ''
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      if (!props.isAdmin)
                        pushClevertapEvent(
                          clevertapEventConfigList.SIDEBAR_TAB_CLICKED_SETTINGS
                        );
                      push(generatePath(UiRoutes.SETTINGS));
                    }}
                    isCollapsed={isCollapsed}
                    label={'Settings'}
                    imgSrc={imgURL['setting-icon']}
                    imgAlt={'Settings'}
                    icon={Icons.settingsSidebar}
                  />
                </ul>
              </div>
            </div>
          </>
        );
      case PRODUCTS.EDGE:
        return getSideBarMenusForEdgeVersions(props?.merchantVersion);

      case PRODUCTS.PAYOUT:
        return (
          <>
            <div
              className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'} customScroll ${!isCollapsed ? 'horizontal-padding' : ''
                }`}
            >
              <div className='manu-block last-element'>
                {!isCollapsed && <h6 className='category-heading'>PAYOUTS</h6>}
                <ul className='list-unstyled components'>
                  {payoutsOptionsList.map((item: Options, index: number) => {
                    return (
                      <ListBlock
                        className={
                          (pathLocation.pathname.indexOf(item.value) > -1 &&
                            'active') ||
                          ''
                        }
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          push(generatePath(`${item.value}`));
                          pushClevertapEvent({
                            eventName: item.clevertapEventName,
                          });
                        }}
                        isCollapsed={isCollapsed}
                        label={item.label}
                        imgSrc={item.icon}
                        imgAlt={item.label}
                        icon={item.iconName}
                      />
                    );
                  })}
                </ul>
              </div>
            </div>{' '}
          </>
        );
      case PRODUCTS.SUBSCRIPTIONS:
        return (
          <>
            <div
              className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'} customScroll ${!isCollapsed ? 'horizontal-padding' : ''
                }`}
            >
              <div className='manu-block last-element'>
                {!isCollapsed && <h6 className='category-heading'>SUBSCRIPTIONS</h6>}
                <ul className='list-unstyled components'>
                  {subscriptionOptionsList.map((item: Options, index: number) => {
                    return (
                      <ListBlock
                        className={
                          (pathLocation.pathname.indexOf(item.value) > -1 &&
                            'active') ||
                          ''
                        }
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          push(generatePath(`${item.value}`));
                          pushClevertapEvent({
                            eventName: item.clevertapEventName
                          });
                        }}
                        isCollapsed={isCollapsed}
                        label={item.label}
                        imgSrc={item.icon}
                        imgAlt={item.label}
                      />
                    );
                  })}
                </ul>
              </div>
            </div>
          </>
        );
      case PRODUCTS.COMPLETE_PROFILE:
        return getCompleteProfileSectionSideBar();
      default:
        return <></>;
    }
  };

  const getAdminList = () => {
    let list: Options[] = [];
    if (props?.roleState?.roleName === OPS_ADMIN) {
      list = [...opsAdminOptionList];
    } else if (checkPermissions('VIEW_OPS_PORTAL')) {
      list = [...adminOptionListWithOps];
    } else list = [...adminOptionList];
    if (
      checkPermissions('MIGRATE_CHECKOUT_MERCHANTS') &&
      getIsAdminFromStore()
    ) {
      if (!list.find((item) => item.label === 'Checkout Version')) {
        list.push(merchantVersionUpdateTab);
      }
    }
    if (
      checkPermissions('UPDATE_KNOWLEDGE_BASE') &&
      !list.find((item) => item.value === UiRoutes.KNOWLEDGE_BASE_ADMIN)
    ) {
      list.push(knowledgeBaseTab);
    }
    return list;
  };

  const activePgRouterAdminToolValue = useMemo(
    () => getActivePgRouterAdminLinkValue(pathLocation?.pathname || ''),
    [pathLocation?.pathname]
  );

  const isPgRouterAdminToolActive = useCallback(
    (value: string) => activePgRouterAdminToolValue === value,
    [activePgRouterAdminToolValue]
  );

  const isPgRouterAdminRouteActive = useMemo(
    () => pgRouterAdminToolsList.some((item) => isPgRouterAdminToolActive(item.value)),
    [isPgRouterAdminToolActive]
  );

  useEffect(() => {
    if (isPgRouterAdminRouteActive) {
      setIsPgRouterAdminGroupOpen(true);
    }
  }, [isPgRouterAdminRouteActive]);

  const keepPgRouterSidebarItemInView = useCallback(() => {
    const scrollContainer = adminMenuScrollRef.current;
    const activeItem = activePgRouterAdminItemRef.current || pgRouterAdminGroupRef.current;

    if (!scrollContainer || !activeItem) {
      return;
    }

    const topPadding = 8;
    const bottomPadding = 8;
    let itemTop = activeItem.offsetTop;
    let offsetParent = activeItem.offsetParent as HTMLElement | null;

    while (offsetParent && offsetParent !== scrollContainer) {
      itemTop += offsetParent.offsetTop;
      offsetParent = offsetParent.offsetParent as HTMLElement | null;
    }

    const itemBottom = itemTop + activeItem.offsetHeight;
    const visibleTop = scrollContainer.scrollTop;
    const visibleBottom = visibleTop + scrollContainer.clientHeight;

    if (itemTop < visibleTop + topPadding) {
      scrollContainer.scrollTop = Math.max(0, itemTop - topPadding);
      return;
    }

    if (itemBottom > visibleBottom - bottomPadding) {
      scrollContainer.scrollTop = itemBottom - scrollContainer.clientHeight + bottomPadding;
    }
  }, []);

  useEffect(() => {
    if (!isPgRouterAdminRouteActive) {
      return;
    }

    keepPgRouterSidebarItemInView();

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(keepPgRouterSidebarItemInView);
    });
    const settledTimeout = window.setTimeout(keepPgRouterSidebarItemInView, 300);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(settledTimeout);
    };
  }, [
    activePgRouterAdminToolValue,
    isPgRouterAdminGroupOpen,
    isPgRouterAdminRouteActive,
    keepPgRouterSidebarItemInView,
  ]);

  const renderPgRouterAdminGroup = () => {
    const pgRouterLandingPath = pgRouterAdminToolsList[0]?.value || UiRoutes.PG_ROUTER_ADMIN;

    if (isCollapsed) {
      return (
        <ListBlock
          className={isPgRouterAdminRouteActive ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            navigateFromAdminSidebar(pgRouterLandingPath);
          }}
          isCollapsed={isCollapsed}
          label='PG Router'
          icon={Icons.smartRouting}
        />
      );
    }

    return (
      <li
        ref={pgRouterAdminGroupRef}
        className={`sidebar-menu-group ${isPgRouterAdminRouteActive ? 'active' : ''}`}
      >
        <a
          href=''
          aria-expanded={isPgRouterAdminGroupOpen}
          className='sidebar-menu-group-toggle border-radius-8'
          onClick={(e) => {
            e.preventDefault();
            preserveAdminSidebarScroll();
            setIsPgRouterAdminGroupOpen((isOpen) => !isOpen);
          }}
        >
          <SVGIcon
            icon={Icons.smartRouting}
            className={isPgRouterAdminRouteActive ? 'active' : ''}
          />
          <label className={isPgRouterAdminRouteActive ? 'active' : ''}>PG Router</label>
          <span
            className={`sidebar-menu-group-chevron ${isPgRouterAdminGroupOpen ? 'open' : ''
              }`}
          />
        </a>
        {isPgRouterAdminGroupOpen && (
          <ul className='list-unstyled components sidebar-menu-subgroup'>
            {pgRouterAdminToolsList
              .filter((item) => {
                if (item.label === 'Observability') return checkPermissions('OBSERVABILITY READ');
                if (item.label === 'Sub-System Mapping') return checkPermissions('OBSERVABILITY READ');
                return hasPaypredictPermission;
              })
              .map((item: Options, index: number) => {
                const isActive = isPgRouterAdminToolActive(item.value);
                return (
                  <ListBlock
                    ref={isActive ? activePgRouterAdminItemRef : undefined}
                    className={isActive ? 'active' : ''}
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateFromAdminSidebar(item.value);
                    }}
                    isCollapsed={isCollapsed}
                    label={item.label}
                    imgSrc={item.icon}
                    imgAlt={item.label}
                    icon={item.iconName}
                  />
                );
              })}
          </ul>
        )}
      </li>
    );
  };

  const renderPartnerMerchantOptions = () => {
    return <div
      className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'} customScroll ${!isCollapsed ? 'horizontal-padding' : ''}`}>

      <div className='manu-block'>
        <ul className='list-unstyled components new-class'>
          {partnerOptionList.map((item: Options, index: number) => {
            return (
              <ListBlock
                className={
                  (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                }
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  push(generatePath(`${item.value}`));
                }}
                isCollapsed={isCollapsed}
                label={item.label}
                imgSrc={item.icon}
                imgAlt={item.label}
                icon={item.iconName}
              />
            );
          })}
        </ul>
      </div>
      <div className='manu-block'>
        <ul className='list-unstyled components new-class'>
          {filteredPartnerOptions.map((item: Options, index: number) => {
            return (
              <ListBlock
                className={
                  (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                }
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  push(generatePath(`${item.value}`));
                }}
                isCollapsed={isCollapsed}
                label={item.label}
                imgSrc={item.icon}
                imgAlt={item.label}
                icon={item.iconName}
              />
            );
          })}
        </ul>
      </div>
      <div className='manu-block last last-element'>
        <ul className='list-unstyled components'>
          <ListBlock
            className={
              (pathLocation.pathname.indexOf(UiRoutes.PARTNER_SETTINGS) >
                -1 &&
                'active') ||
              ''
            }
            onClick={(e) => {
              e.preventDefault();
              if (!props.isAdmin)
                pushClevertapEvent({
                  eventName:
                    CleverTapEventNames.SIDEBAR_TAB_CLICKED_SETTINGS,
                });
              push(generatePath(UiRoutes.PARTNER_SETTINGS));
            }}
            isCollapsed={isCollapsed}
            label={'Settings'}
            imgSrc={imgURL['partner-settings']}
            imgAlt={'Partner Setting'}
            icon={Icons.settingsSidebar}
          />
        </ul>
      </div>
    </div>;
  };
  return (
    <React.Fragment>
      <Sider
        collapsed={isCollapsed}
        className='sidebar'
        theme='light'
        width={272}
      >
        <div
          className={
            isCiti
              ? 'sidebar-header-citi'
              : props.isPartnerMerchant
                ? 'sidebar-header-partner'
                : 'sidebar-header'
          }
        >
          {(props.isAdmin && !props.merchantId) || isCiti ? (
            <>
              {isCollapsed ? (
                <div className={isCiti ? 'citi-collapsed' : 'plural-collapsed'}>
                  {/* <SVGIcon icon={isCiti ? Icons.citiLogo : Icons.pluralLogo} /> */}
                  {isCiti ? <SVGIcon icon={Icons.citiLogo} /> : <img src={imgURL['plural-online']} />}
                </div>
              ) : (
                <div className={isCiti ? 'citi-logo' : ''}>
                  {/* <SVGIcon icon={isCiti ? Icons.citiLogo : Icons.pluralLogo} /> */}
                  {isCiti ? <SVGIcon icon={Icons.citiLogo} /> : <img src={imgURL['plural-online']} />}
                </div>
              )}
            </>
          ) :
            showPartnerBrandHeader
              ? (
                <>
                  {isCollapsed ? (
                    <img
                      src={partnerBrandLogo}
                      className='partner-logo-collapsed'
                      alt='Partner Logo'
                    />
                  ) : (
                    <div className='partner-header-images'>
                      <img
                        src={partnerBrandLogo}
                        className='partner-logo'
                        alt='Partner Logo'
                      />
                    </div>
                  )}
                </>
              ) :
              (
                <h3>
                  {/* <a href='#'> */}
                  <HeaderDropdown />
                  {/* </a> */}
                </h3>
              )}
        </div>
        {props.isAdmin && !props.merchantId ? (
          <>
            <div
              ref={adminMenuScrollRef}
              onScroll={handleAdminSidebarScroll}
              className={`${!isCollapsed ? 'menu-section' : 'menu-section-collapsed'
                } customScroll ${!isCollapsed ? 'horizontal-padding' : ''}`}
            >
              <div className='manu-block'>
                <ul className='list-unstyled components new-class'>
                  {getAdminList()?.map((item: Options, index: number) => {
                    return (
                      <ListBlock
                        className={
                          (pathLocation.pathname.indexOf(item.value) > -1 && 'active') || ''
                        }
                        key={index}
                        onClick={(e) => {
                          e.preventDefault();
                          navigateFromAdminSidebar(item.value);
                        }}
                        isCollapsed={isCollapsed}
                        label={item.label}
                        imgSrc={item.icon}
                        imgAlt={item.label}
                        icon={item.iconName}
                      />
                    );
                  })}
                </ul>
              </div>
              <>
                <div className='manu-block'>
                  {isCollapsed ? (
                    ''
                  ) : (
                    <h6 className='category-header'>RCU Dashboard</h6>
                  )}
                  <ul className='list-unstyled components'>
                    {rcuOptionSearchList.map(
                      (item: Options, index: number) => {
                        return (
                          <ListBlock
                            className={
                              pathLocation.pathname.indexOf(item.value) >
                                -1 ? 'active' : ''
                            }
                            key={index}
                            onClick={(e) => {
                              e.preventDefault();
                              navigateFromAdminSidebar(item.value);
                            }}
                            isCollapsed={isCollapsed}
                            label={item.label}
                            imgSrc={item.icon}
                            imgAlt={item.label}
                            icon={item.iconName}
                          />
                        );
                      }
                    )}
                  </ul>
                </div>
                {shouldRenderAdminDashboardSection && (
                  <div className='manu-block'>
                    {isCollapsed ? (
                      ''
                    ) : (
                      <h6 className='category-header'>Admin Dashboard</h6>
                    )}
                    <ul className='list-unstyled components'>
                      {adminDashboardOptionList.map(
                        (item: Options, index: number) => {
                          return (
                            <ListBlock
                              className={
                                pathLocation.pathname.indexOf(item.value) >
                                -1 && 'active'
                              }
                              key={index}
                              onClick={(e) => {
                                e.preventDefault();
                                navigateFromAdminSidebar(item.value);
                              }}
                              isCollapsed={isCollapsed}
                              label={item.label}
                              imgSrc={item.icon}
                              imgAlt={item.label}
                              icon={item.iconName}
                            />
                          );
                        }
                      )}
                      {canAccessPgRouterAdmin && renderPgRouterAdminGroup()}
                    </ul>
                  </div>
                )}
              </>
            </div>
          </>
        ) : ((props.isPartnerMerchant && (props.merchantId === 0 || props.merchantId === props.partnerId))) ? (
          renderPartnerMerchantOptions()
        ) : (
          sidebarMenu()
        )}

        {/* <!--Toggle button--> */}
        {!onSelfServePage && <button
          type='button'
          id='sidebarCollapse'
          className='navbar-btn sidebarCollapse'
          onClick={() => {
            props.updateSidebarView();
          }}
        >
          <img
            src={imgURL['collapse-icon']}
            alt='Collapse'
            className={(isCollapsed && 'sidebar-rotated-expand-icon') || ''}
          />
          {!isCollapsed && <label>Collapse Sidebar</label>}
        </button>}

        {!isCollapsed && isCiti && (
          <div className='powered-by-plural'>
            <img src={imgURL['powered-pinelabs-online']} alt='powered by plural' />
          </div>
        )}

        {isCollapsed && isCiti && (
          <div className='plural-logo-citi'>
            <img src={imgURL['plural-online']} alt='plural logo' />
          </div>
        )}
      </Sider>
    </React.Fragment>
  );
};

const mapStateToProps = ({ sidebarReducer, loginReducer, onboardingReducer, MerchantListingReducer }) => ({
  isCollapsed: sidebarReducer.isCollapsed,
  merchantId: loginReducer.loginState.MerchantId,
  isAdmin: loginReducer.loginState.isAdmin,
  merchantTPVEnabled: loginReducer?.loginState?.merchantTPVEnabled ?? false,
  merchantSubscriptionEnabled:
    loginReducer?.loginState?.merchantSubscriptionEnabled ?? false,
  edgeMerchantPaymentLinkEnabled:
    loginReducer?.loginState?.edgeMerchantPaymentLinkEnabled ?? false,
  roleState: loginReducer?.roleState,
  merchantVersion: loginReducer?.loginState?.merchantVersion,
  isPartnerMerchant: loginReducer?.loginState?.isPartnerMerchant,
  partnerId: loginReducer?.loginState?.partnerId,
  isSelfServe: loginReducer?.loginState?.isSelfServe,
  isLiveMerchant: loginReducer?.loginState?.isLiveMerchant,
  stepperDetails: onboardingReducer.stepperDetails,
  isParentMerchantOne: loginReducer?.loginState?.isParentMerchant ?? false,
  isTransactionMerchant: loginReducer?.loginState?.isTransactingMerchant,
  isBrandWalletEnabled: getMerchantDetailsValue(
    MerchantListingReducer?.merchantOdsData,
    'isBrandWalletEnabled'
  ),
  accountSource:
    getMerchantDetailsValue(
      MerchantListingReducer?.merchantOdsData,
      'accountSource'
    ) ?? null,
  isDisputesEnabled: loginReducer?.loginState?.isDisputesEnabled ?? null,
  sidebarStepClickState: onboardingReducer.sidebarStepClick,
  themingDetails: getDashboardThemeDetails(MerchantListingReducer?.merchantOdsData),
});

export default connect(mapStateToProps, {
  updateSidebarView,
  sidebarStepClickAction,
  clearSidebarStepClickAction,
})(SideBar);
